import { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { api, formatApiError } from "@/lib/api";
import { Card } from "@/components/ui/card";
import { Badge } from "@/components/ui/badge";
import { formatMoney } from "@/lib/money";
import { Wallet, ArrowDownLeft, ArrowUpRight, Receipt, ArrowRight, Loader2 } from "lucide-react";
import { toast } from "sonner";

const IN_COLOR  = "hsl(var(--status-present))";
const OUT_COLOR = "hsl(var(--status-absent))";

export default function PettyCashDashboardPage() {
  const [summary, setSummary] = useState(null);
  const [recent, setRecent] = useState([]);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    (async () => {
      try {
        const [sRes, tRes] = await Promise.all([
          api.get("/petty-cash/summary"),
          api.get("/petty-cash/transactions"),
        ]);
        setSummary(sRes.data);
        setRecent(tRes.data.slice(0, 8));
      } catch (e) {
        toast.error(formatApiError(e));
      } finally { setLoading(false); }
    })();
  }, []);

  return (
    <div className="p-8 lg:p-10 max-w-6xl" data-testid="pc-dashboard">
      <div className="flex items-end justify-between flex-wrap gap-4">
        <div>
          <div className="font-mono-mini text-[11px] uppercase tracking-[0.25em] text-muted-foreground">Petty Cash</div>
          <h1 className="font-display text-4xl sm:text-5xl font-bold tracking-tight mt-2">Cash on hand</h1>
          <p className="text-sm text-muted-foreground mt-2">
            Log receipts and expenses. Balance updates instantly.
          </p>
        </div>
        <Link
          to="/petty-cash/transactions"
          data-testid="pc-add-tx-cta"
          className="inline-flex items-center gap-2 px-5 py-2.5 rounded-xl text-white text-sm font-medium btn-lift shadow-soft hover:shadow-soft-lg"
          style={{ background: "hsl(var(--status-halfday))" }}
        >
          Add transaction <ArrowRight className="h-4 w-4" />
        </Link>
      </div>

      {/* Balance hero */}
      <Card className="mt-10 p-8 rounded-2xl border-border shadow-soft" data-testid="pc-balance-card">
        <div className="flex items-center gap-3">
          <Wallet className="h-4 w-4 text-muted-foreground" />
          <div className="text-[11px] uppercase tracking-[0.2em] text-muted-foreground">Current balance</div>
        </div>
        <div className="mt-3 flex items-end gap-4 flex-wrap">
          <div
            className="font-display text-6xl sm:text-7xl font-bold tabular-nums tracking-tighter"
            data-testid="pc-current-balance"
            style={{ color: (summary?.balance ?? 0) < 0 ? OUT_COLOR : "hsl(var(--foreground))" }}
          >
            {loading ? "—" : formatMoney(summary?.balance || 0)}
          </div>
          {summary && (
            <div className="pb-2 flex items-center gap-2 flex-wrap">
              <Badge variant="outline" className="rounded-full font-mono-mini gap-1" style={{ color: IN_COLOR }}>
                <ArrowDownLeft className="h-3 w-3" />
                In {formatMoney(summary.total_in)}
              </Badge>
              <Badge variant="outline" className="rounded-full font-mono-mini gap-1" style={{ color: OUT_COLOR }}>
                <ArrowUpRight className="h-3 w-3" />
                Out {formatMoney(summary.total_out)}
              </Badge>
              <Badge variant="outline" className="rounded-full font-mono-mini">
                {summary.tx_count} tx total
              </Badge>
            </div>
          )}
        </div>
      </Card>

      {/* KPIs */}
      <div className="grid grid-cols-2 md:grid-cols-4 gap-4 mt-6">
        <Card className="p-5 rounded-2xl border-border shadow-soft" data-testid="pc-kpi-today-in">
          <div className="text-[11px] uppercase tracking-[0.18em] text-muted-foreground">Today · In</div>
          <div className="font-display text-3xl font-bold tabular-nums mt-3" style={{ color: IN_COLOR }}>
            {loading ? "—" : formatMoney(summary?.today_in || 0)}
          </div>
        </Card>
        <Card className="p-5 rounded-2xl border-border shadow-soft" data-testid="pc-kpi-today-out">
          <div className="text-[11px] uppercase tracking-[0.18em] text-muted-foreground">Today · Out</div>
          <div className="font-display text-3xl font-bold tabular-nums mt-3" style={{ color: OUT_COLOR }}>
            {loading ? "—" : formatMoney(summary?.today_out || 0)}
          </div>
        </Card>
        <Card className="p-5 rounded-2xl border-border shadow-soft" data-testid="pc-kpi-month-in">
          <div className="text-[11px] uppercase tracking-[0.18em] text-muted-foreground">This month · In</div>
          <div className="font-display text-3xl font-bold tabular-nums mt-3" style={{ color: IN_COLOR }}>
            {loading ? "—" : formatMoney(summary?.month_in || 0)}
          </div>
        </Card>
        <Card className="p-5 rounded-2xl border-border shadow-soft" data-testid="pc-kpi-month-out">
          <div className="text-[11px] uppercase tracking-[0.18em] text-muted-foreground">This month · Out</div>
          <div className="font-display text-3xl font-bold tabular-nums mt-3" style={{ color: OUT_COLOR }}>
            {loading ? "—" : formatMoney(summary?.month_out || 0)}
          </div>
        </Card>
      </div>

      {/* Recent transactions */}
      <Card className="mt-8 p-0 rounded-2xl border-border shadow-soft overflow-hidden">
        <div className="px-5 py-4 border-b border-border flex items-center justify-between">
          <div className="flex items-center gap-2">
            <Receipt className="h-4 w-4 text-muted-foreground" />
            <div className="text-sm font-medium">Recent transactions</div>
          </div>
          <Link to="/petty-cash/transactions" className="text-xs text-muted-foreground hover:text-foreground" data-testid="pc-see-all-tx">
            See all →
          </Link>
        </div>
        {loading ? (
          <div className="py-10 text-center text-sm text-muted-foreground">
            <Loader2 className="h-4 w-4 animate-spin inline-block mr-2" /> Loading…
          </div>
        ) : recent.length === 0 ? (
          <div className="py-12 text-center">
            <div className="text-sm text-muted-foreground">No transactions yet.</div>
            <Link
              to="/petty-cash/transactions"
              className="inline-flex items-center gap-2 mt-4 px-5 py-2.5 rounded-xl text-white text-sm font-medium btn-lift shadow-soft"
              style={{ background: "hsl(var(--status-halfday))" }}
              data-testid="pc-empty-add-cta"
            >
              Log the first one <ArrowRight className="h-4 w-4" />
            </Link>
          </div>
        ) : (
          <ul className="divide-y divide-border">
            {recent.map((t) => (
              <li
                key={t.tx_id}
                data-testid={`pc-recent-${t.tx_id}`}
                className="px-5 py-3 flex items-center gap-4"
              >
                <div
                  className="h-9 w-9 rounded-md grid place-items-center"
                  style={{ background: `${t.kind === "in" ? IN_COLOR : OUT_COLOR}18`, color: t.kind === "in" ? IN_COLOR : OUT_COLOR }}
                >
                  {t.kind === "in" ? <ArrowDownLeft className="h-4 w-4" /> : <ArrowUpRight className="h-4 w-4" />}
                </div>
                <div className="min-w-0 flex-1">
                  <div className="text-sm font-medium truncate">{t.head_name}</div>
                  <div className="text-[11px] text-muted-foreground truncate">
                    {t.date}
                    {t.voucher_no ? ` · V# ${t.voucher_no}` : ""}
                    {t.note ? ` · ${t.note}` : ""}
                  </div>
                </div>
                <div
                  className="font-mono-mini tabular-nums text-sm font-medium"
                  style={{ color: t.kind === "in" ? IN_COLOR : OUT_COLOR }}
                >
                  {t.kind === "in" ? "+" : "−"} {formatMoney(t.amount)}
                </div>
              </li>
            ))}
          </ul>
        )}
      </Card>
    </div>
  );
}
